import { useForm } from "react-hook-form";
import { PapInput, PapErrors } from '../components/FormElements';
import { useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { motion } from 'framer-motion';
import { ShieldCheck, Save } from 'lucide-react';
import useApi from "../hooks/useApi";
import AuthLayout from '../components/AuthLayout';

interface ResetPasswordFormData {
  password: string;
}

const schema = yup.object().shape({
  password: yup.string().required('Password is required').min(6, 'Password must be at least 6 characters')
});

const stagger = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.15 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.23, 1, 0.32, 1] as const } },
};

const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const api = useApi();
  const history = useHistory();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<ResetPasswordFormData>({
    resolver: yupResolver(schema)
  });

  const resetPassword = async (data: ResetPasswordFormData) => {
    try {
      const result = await api.patch(`/api/v1/passwords/${token}`, data);
      if (result) {
        setPageErrors([]);
        history.push('/login?reset=1');
      }
    } catch (ex: any) {
      if (ex.response) {
        const exResponseBody = await ex.response.json();
        setPageErrors([exResponseBody.error]);
      }
    }
  };

  return (
    <AuthLayout>
      <motion.div variants={stagger} initial="hidden" animate="show">
        {/* Icon & heading */}
        <motion.div variants={fadeUp} className="text-center mb-8">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
            <ShieldCheck className="w-7 h-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground mb-1.5">
            Set new password
          </h1>
          <p className="text-sm text-muted-foreground">
            Enter your new password below.
          </p>
        </motion.div>

        {/* Errors */}
        {pageErrors.length > 0 && (
          <motion.div variants={fadeUp}>
            <PapErrors errors={pageErrors} />
          </motion.div>
        )}

        {/* Form */}
        <motion.form variants={fadeUp} noValidate onSubmit={handleSubmit(resetPassword)} method="post">
          <PapInput fieldLabel="New Password" type="password" formBinding={register("password")} placeholder="" autocomplete={false} fieldName="password" error={errors.password} required={true} />

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary btn-block py-3 text-base font-semibold gap-2 disabled:opacity-50"
          >
            {isSubmitting ? (
              <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isSubmitting ? 'Saving...' : 'Save New Password'}
          </button>
        </motion.form>

        {/* Back to login */}
        <motion.p variants={fadeUp} className="text-center text-sm text-muted-foreground mt-6">
          <Link to="/login" className="text-accent hover:text-accent/80 hover:underline font-semibold transition-colors">
            Back to Sign In
          </Link>
        </motion.p>
      </motion.div>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
